import { useEffect } from "react";

const SECTION_IDS = ["skills", "projects", "experience"];
const LERP = 0.14;

const lerp = (current, target, amount) =>
  current + (target - current) * amount;

export const useParallax3D = () => {
  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reducedMotion) return undefined;

    const hero = document.querySelector(".hero-v2");
    const shapes = Array.from(document.querySelectorAll(".floating-shapes .shape"));
    const spotlight = document.querySelector(".hero-spotlight");
    const spotlightCard = document.querySelector(".hero-spotlight-card");
    const heroContent = document.querySelector(".hero-depth-stage");
    const heroText = document.querySelector(".hero-text-v2");
    const ambient = document.querySelector(".hero-ambient");
    const orbs = Array.from(document.querySelectorAll(".hero-orb"));
    const beam = document.querySelector(".hero-beam");
    const grid = document.querySelector(".hero-grid");
    const interactiveGlow = document.querySelector(".hero-interactive-glow");
    const depthFocus = document.querySelector(".hero-depth-focus");

    if (hero) hero.classList.add("hero--interactive");
    if (ambient) ambient.classList.add("hero-ambient--interactive");

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = null;
    let isPointerInside = false;

    const setPointerFromClient = (clientX, clientY) => {
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;

      targetX = (clientX - rect.left) / rect.width - 0.5;
      targetY = (clientY - rect.top) / rect.height - 0.5;
      isPointerInside = true;
    };

    const handleMouseMove = (event) => {
      setPointerFromClient(event.clientX, event.clientY);
    };

    const handleTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      setPointerFromClient(touch.clientX, touch.clientY);
    };

    const handlePointerLeave = () => {
      isPointerInside = false;
      targetX = 0;
      targetY = 0;
    };

    const update = () => {
      currentX = lerp(currentX, targetX, LERP);
      currentY = lerp(currentY, targetY, LERP);

      const scrolled = window.scrollY;
      const heroRect = hero?.getBoundingClientRect();
      const heroVisible =
        heroRect && heroRect.bottom > 0 && heroRect.top < window.innerHeight;

      if (heroVisible && hero) {
        const glowX = 50 + currentX * 28;
        const glowY = 42 + currentY * 22;
        const focusX = 50 + currentX * 38;
        const focusY = 45 + currentY * 32;
        hero.style.setProperty("--hero-glow-x", `${glowX}%`);
        hero.style.setProperty("--hero-glow-y", `${glowY}%`);
        hero.style.setProperty("--hero-focus-x", `${focusX}%`);
        hero.style.setProperty("--hero-focus-y", `${focusY}%`);
        hero.style.setProperty("--hero-mx", `${currentX}`);
        hero.style.setProperty("--hero-my", `${currentY}`);
        hero.style.setProperty(
          "--hero-depth-blur",
          isPointerInside ? "1" : "0"
        );
      }

      if (heroVisible && depthFocus) {
        depthFocus.style.opacity = isPointerInside ? "0.5" : "0";
      }

      if (heroVisible && interactiveGlow) {
        const boost = isPointerInside ? 1.3 : 0.5;
        const px = 50 + currentX * 44;
        const py = 48 + currentY * 34;
        interactiveGlow.style.left = `${px}%`;
        interactiveGlow.style.top = `${py}%`;
        interactiveGlow.style.opacity = isPointerInside ? "0.85" : "0.18";
        interactiveGlow.style.transform = `translate3d(-50%, -50%, 60px) scale(${
          (1 + Math.abs(currentX) * 0.12 + Math.abs(currentY) * 0.08) * boost
        })`;
      }

      if (heroVisible && ambient) {
        ambient.style.transform = `translate3d(${currentX * -18}px, ${
          currentY * -12
        }px, -120px) rotateX(${currentY * 4}deg) rotateY(${currentX * -5}deg)`;
      }

      if (heroVisible && orbs.length) {
        const time = performance.now() * 0.001;
        const pointerBoost = isPointerInside ? 1.75 : 1;
        orbs.forEach((orb, index) => {
          const depth = 0.55 + index * 0.22;
          const idleX = Math.sin(time * 0.42 + index * 1.3) * 18;
          const idleY = Math.cos(time * 0.36 + index * 0.85) * 14;
          const moveX = currentX * 132 * depth * pointerBoost + idleX;
          const moveY = currentY * 94 * depth * pointerBoost + idleY;
          const scale = 1 + depth * 0.05 + Math.sin(time * 0.5 + index) * 0.03;
          const z = 40 + index * 55;

          orb.style.transform = `translate3d(${moveX}px, ${moveY}px, ${z}px) scale(${scale})`;
        });
      }

      if (heroVisible && beam) {
        const beamX = currentX * 72;
        const beamY = currentY * 38;
        const beamRotate = -8 + currentX * 11;
        beam.style.transform = `translate3d(${beamX}px, ${beamY}px, -40px) rotate(${beamRotate}deg)`;
      }

      if (heroVisible && grid) {
        const gridX = currentX * -56;
        const gridY = currentY * -42 + scrolled * 0.04;
        grid.style.backgroundPosition = `${gridX}px ${gridY}px`;
        grid.style.transform = `translate3d(${currentX * 20}px, ${
          currentY * 14
        }px, -90px)`;
      }

      if (heroVisible && shapes.length) {
        const time = performance.now() * 0.001;
        shapes.forEach((shape, index) => {
          const depth = parseFloat(shape.dataset.depth || `${0.35 + index * 0.2}`);
          const scrollShift = scrolled * depth * 0.12;
          const idleX = Math.sin(time * 0.45 + index * 1.4) * 16 * depth;
          const idleY = Math.cos(time * 0.38 + index * 0.9) * 12 * depth;
          const moveX = currentX * 52 * depth + idleX;
          const moveY = currentY * 36 * depth - scrollShift + idleY;
          const rotateX = currentY * -14 * depth + Math.sin(time * 0.3 + index) * 2;
          const rotateY = currentX * 16 * depth + Math.cos(time * 0.28 + index) * 2.5;
          const scale = 1 + depth * 0.04 + Math.sin(time * 0.5 + index) * 0.02;
          const z = depth * 90 - 30;

          shape.style.transform = `translate3d(${moveX}px, ${moveY}px, ${z}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`;
        });
      }

      if (heroVisible && heroContent) {
        const stageTiltX = currentY * -3;
        const stageTiltY = currentX * 4;
        heroContent.style.transform = `perspective(1200px) rotateX(${stageTiltX}deg) rotateY(${stageTiltY}deg)`;
      }

      if (heroVisible && spotlight) {
        const tiltX = currentY * -11;
        const tiltY = currentX * 13;
        const lift = Math.min(scrolled * 0.06, 36);
        spotlight.style.transform = `perspective(1100px) translate3d(${
          currentX * -10
        }px, ${-lift + currentY * 6}px, 48px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
      }

      if (heroVisible && spotlightCard) {
        const cardTiltX = currentY * 4;
        const cardTiltY = currentX * -5;
        spotlightCard.style.transform = `translate3d(${currentX * 6}px, ${
          currentY * 4
        }px, 24px) rotateX(${cardTiltX}deg) rotateY(${cardTiltY}deg)`;
      }

      if (heroVisible && heroText) {
        const textShift = Math.min(scrolled * 0.04, 24);
        const tiltX = currentY * -7;
        const tiltY = currentX * 9;
        heroText.style.transform = `perspective(1000px) translate3d(${
          currentX * -12
        }px, ${textShift + currentY * 4}px, 32px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
      }

      SECTION_IDS.forEach((id, index) => {
        const section = document.getElementById(id);
        if (!section) return;

        const rect = section.getBoundingClientRect();
        if (rect.top >= window.innerHeight || rect.bottom <= 0) return;

        const progress =
          (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const offset = (progress - 0.5) * 90 * (1 + index * 0.08);
        const xOffset = (progress - 0.5) * 24 * (index % 2 === 0 ? 1 : -1);
        const rotate = (progress - 0.5) * 4;

        section.style.setProperty("--parallax-offset", `${offset}px`);
        section.style.setProperty("--parallax-x", `${xOffset}px`);
        section.style.setProperty("--parallax-rotate", `${rotate}deg`);
      });
    };

    const tick = () => {
      update();
      const heroRect = hero?.getBoundingClientRect();
      const heroVisible =
        heroRect && heroRect.bottom > 0 && heroRect.top < window.innerHeight;
      const needsReturn =
        Math.abs(currentX) > 0.001 ||
        Math.abs(currentY) > 0.001 ||
        Math.abs(targetX) > 0.001 ||
        Math.abs(targetY) > 0.001;

      if (heroVisible || needsReturn) {
        rafId = window.requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };

    const startLoop = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(tick);
      }
    };

    window.addEventListener("scroll", startLoop, { passive: true });
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    hero?.addEventListener("mouseleave", handlePointerLeave);
    hero?.addEventListener("touchend", handlePointerLeave);
    startLoop();

    return () => {
      window.removeEventListener("scroll", startLoop);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
      hero?.removeEventListener("mouseleave", handlePointerLeave);
      hero?.removeEventListener("touchend", handlePointerLeave);
      if (rafId !== null) window.cancelAnimationFrame(rafId);

      hero?.classList.remove("hero--interactive");
      ambient?.classList.remove("hero-ambient--interactive");
      hero?.style.removeProperty("--hero-glow-x");
      hero?.style.removeProperty("--hero-glow-y");
      hero?.style.removeProperty("--hero-focus-x");
      hero?.style.removeProperty("--hero-focus-y");
      hero?.style.removeProperty("--hero-mx");
      hero?.style.removeProperty("--hero-my");
      hero?.style.removeProperty("--hero-depth-blur");

      shapes.forEach((shape) => {
        shape.style.transform = "";
      });
      orbs.forEach((orb) => {
        orb.style.transform = "";
      });
      if (beam) beam.style.transform = "";
      if (grid) {
        grid.style.backgroundPosition = "";
        grid.style.transform = "";
      }
      if (ambient) ambient.style.transform = "";
      if (interactiveGlow) {
        interactiveGlow.style.left = "";
        interactiveGlow.style.top = "";
        interactiveGlow.style.opacity = "";
        interactiveGlow.style.transform = "";
      }
      if (depthFocus) depthFocus.style.opacity = "";
      if (spotlight) spotlight.style.transform = "";
      if (spotlightCard) spotlightCard.style.transform = "";
      if (heroContent) heroContent.style.transform = "";
      if (heroText) heroText.style.transform = "";

      SECTION_IDS.forEach((id) => {
        const section = document.getElementById(id);
        if (!section) return;
        section.style.removeProperty("--parallax-offset");
        section.style.removeProperty("--parallax-x");
        section.style.removeProperty("--parallax-rotate");
      });
    };
  }, []);
};
